import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { NavMarker } from 'react-rhumb';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Select } from 'foundations-components/transitional/components/Select';
import ContentRevenueAttributionCard from 'content-management-details-lib/components/reporting/ContentRevenueAttributionCard';
import PerformanceSection from 'content-management-details-lib/components/reporting/PerformanceSection';
import SourcesChart from 'content-management-details-lib/components/reporting/SourcesChart';
import SourcesDataWell from 'content-management-details-lib/components/reporting/SourcesDataWell';
import SourcesTable from 'content-management-details-lib/components/reporting/SourcesTable';
import { ALL_VARIATIONS } from '../constants/abVariations';
import { METRICS_TO_DISPLAY_IN_DATA_WELL } from '../constants/reporting';
import useTestResultsData from '../hooks/useTestResultsData';
import PerformanceEmptyState from './PerformanceEmptyState';
import { useTrackPageView } from '../hooks/useTrackPageView';
import VideoPerformance from 'content-management-details-lib/components/reporting/VideoPerformance';
import useAnalyticsPageId from '../hooks/useAnalyticsPageId';
import { resolveAnalyticsPageId } from '../utils/dynamicPageUtils';
import useQueryParams from '../hooks/useQueryParams';
import { useUpdateQueryParams } from '../hooks/useUpdateQueryParams';
import { useReportingFiltersProvider } from 'content-management-details-lib/providers/ReportingFiltersProvider';
// @ts-expect-error Untyped module
import getDefaultFrequency from 'reporting-ui-components/lib/getDefaultFrequency';
import { usePersonalizationVariantsData } from '../hooks/usePersonalizationVariantsData';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const VariationsFilter = ({
  variants,
  onChangeVariation,
  selectedVariation
}) => () => {
  const options = variants.reduce((acc, {
    id,
    name
  }) => {
    acc.push({
      text: name,
      value: id
    });
    return acc;
  }, [{
    text: I18n.text('page-details-ui.reporting.header.variationsFilter.allOption'),
    value: ALL_VARIATIONS
  }]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: "page-details-ui.reporting.header.variationsFilter.label"
    }), /*#__PURE__*/_jsx(Select, {
      "data-test-id": "variations-filter",
      buttonUse: "transparent",
      padding: "0",
      marginInline: "300",
      menuWidth: "auto",
      onChange: e => onChangeVariation(e.target.value),
      options: options,
      value: selectedVariation
    })]
  });
};
const PerformanceCharts = () => {
  const {
    liveAbVariants
  } = useTestResultsData();
  const {
    personalizationVariants
  } = usePersonalizationVariantsData();
  const analyticsPageId = useAnalyticsPageId();
  const [selectedVariation, setSelectedVariation] = useState(analyticsPageId);
  const onChangeVariation = value => {
    setSelectedVariation(value);
  };
  const queryParams = useQueryParams();
  const updateQueryParams = useUpdateQueryParams();
  const {
    dateRange: providerDateRange,
    frequency: providerFrequency,
    handleDateRangeChange: providerHandleDateRangeChange,
    setFrequency: providerSetFrequency
  } = useReportingFiltersProvider();
  const [currentDateRange, setCurrentDateRange] = useState({
    rangeType: queryParams.get('rangeType') || providerDateRange.rangeType,
    startDate: queryParams.get('startDate') || providerDateRange.startDate,
    endDate: queryParams.get('endDate') || providerDateRange.endDate
  });
  const [currentFrequency, setCurrentFrequency] = useState(queryParams.get('frequency') || providerFrequency);
  const handleFrequencyChange = useCallback(newFrequency => {
    setCurrentFrequency(newFrequency);
    providerSetFrequency(newFrequency);
    updateQueryParams({
      frequency: newFrequency
    });
  }, [providerSetFrequency, updateQueryParams]);
  const handleDateRangeChange = useCallback(newDateRange => {
    setCurrentDateRange(newDateRange);
    providerHandleDateRangeChange(newDateRange);
    const defaultFrequency = getDefaultFrequency(newDateRange);
    handleFrequencyChange(defaultFrequency);
    const queryParamsToUpdate = {
      rangeType: newDateRange.rangeType,
      frequency: defaultFrequency
    };
    if (newDateRange.rangeType === 'CUSTOM') {
      queryParamsToUpdate.startDate = newDateRange.startDate || undefined;
      queryParamsToUpdate.endDate = newDateRange.endDate || undefined;
    } else {
      queryParamsToUpdate.startDate = undefined;
      queryParamsToUpdate.endDate = undefined;
    }
    updateQueryParams(queryParamsToUpdate);
  }, [providerHandleDateRangeChange, handleFrequencyChange, updateQueryParams]);
  const hasMounted = useRef(false);
  useEffect(() => {
    if (!hasMounted.current) {
      if (currentDateRange) {
        handleDateRangeChange(currentDateRange);
      }
      if (currentFrequency) {
        handleFrequencyChange(currentFrequency);
      }
      hasMounted.current = true;
    }
  }, [currentDateRange, currentFrequency, handleDateRangeChange, handleFrequencyChange]);
  const additionalReportingFilters = useMemo(() => {
    const hasAbVariants = liveAbVariants && liveAbVariants.length > 1;
    const hasPersonalizationVariants = personalizationVariants && personalizationVariants.length > 1;
    if (!hasAbVariants && !hasPersonalizationVariants) {
      return undefined;
    }
    let variants = [];
    if (hasAbVariants) {
      variants = liveAbVariants;
    } else if (hasPersonalizationVariants) {
      variants = personalizationVariants;
    }
    return [VariationsFilter({
      variants,
      selectedVariation,
      onChangeVariation
    })];
  }, [liveAbVariants, personalizationVariants, selectedVariation]);
  const variationIds = useMemo(() => {
    const hasAbVariants = liveAbVariants && liveAbVariants.length > 1;
    const hasPersonalizationVariants = personalizationVariants && personalizationVariants.length > 1;
    if (selectedVariation !== ALL_VARIATIONS || !hasAbVariants && !hasPersonalizationVariants) {
      return undefined;
    }
    let variants = [];
    if (hasAbVariants) {
      variants = liveAbVariants;
    } else if (hasPersonalizationVariants) {
      variants = personalizationVariants;
    }
    return variants.map(v => v.id);
  }, [liveAbVariants, personalizationVariants, selectedVariation]);
  const variationId = selectedVariation === ALL_VARIATIONS ? undefined : selectedVariation;
  const resolvedVariationId = resolveAnalyticsPageId(variationId, window.location.pathname);
  useTrackPageView('performance');
  return /*#__PURE__*/_jsx(PerformanceSection, {
    additionalReportingFilters: additionalReportingFilters,
    analyticsPageId: resolvedVariationId,
    currentDateRange: currentDateRange,
    currentFrequency: currentFrequency,
    onDateRangeChange: handleDateRangeChange,
    onFrequencyChange: handleFrequencyChange,
    renderEmptyState: ({
      contentStatus
    }) => /*#__PURE__*/_jsx(NavMarker, {
      name: "PERFORMANCE_TAB_EMPTY_LOADED",
      children: /*#__PURE__*/_jsx(PerformanceEmptyState, {
        contentStatus: contentStatus
      })
    }),
    children: /*#__PURE__*/_jsxs(NavMarker, {
      name: "PERFORMANCE_TAB_LOADED",
      children: [/*#__PURE__*/_jsx(ContentRevenueAttributionCard, {
        variationIds: variationIds,
        variationId: resolvedVariationId
      }), /*#__PURE__*/_jsx(SourcesDataWell, {
        variationIds: variationIds,
        variationId: resolvedVariationId,
        metricsToDisplay: METRICS_TO_DISPLAY_IN_DATA_WELL
      }), /*#__PURE__*/_jsx(SourcesChart, {
        variationIds: variationIds,
        variationId: resolvedVariationId
      }), /*#__PURE__*/_jsx(SourcesTable, {
        variationIds: variationIds,
        variationId: resolvedVariationId
      }), /*#__PURE__*/_jsx(VideoPerformance, {
        contentId: resolvedVariationId || analyticsPageId
      })]
    })
  });
};
export default PerformanceCharts;